import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, Plus, Send, Edit3, Trash2, X, Search, CheckCircle2, AlertTriangle, ShieldAlert, Lock,
} from 'lucide-react';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import FormBuilder from '../shared/FormBuilder';
import ChoicesEditor from '../shared/ChoicesEditor';
import type { OrgSpecialRequestTemplate, FormBuilderField } from '../../types';
import {
  fetchOrgSpecialRequestTemplates,
  createOrgSpecialRequestTemplate,
  updateOrgSpecialRequestTemplate,
  deleteOrgSpecialRequestTemplate,
  fetchOrgOptions,
  sendOrgSpecialRequestsBulk,
  type OrgOption,
  type OrgBulkSendResult,
  type OrgTemplateOrgType,
} from '../../lib/orgSpecialRequestTemplates';

/**
 * OrgSpecialRequestTemplatesManager — dedicated Organization Special
 * Request Templates (program 2, T1). A template is available ONLY for
 * the organization types it targets; sending fans out into fully
 * independent per-organization request records. This store is fully
 * separate from the applicant special-request templates.
 */

const ORG_TYPES: { value: OrgTemplateOrgType; label: string }[] = [
  { value: 'referrer', label: 'Referrers' },
  { value: 'sponsor', label: 'Sponsors' },
  { value: 'employer', label: 'Employers' },
];

const RESPONSE_TYPES = [
  { value: 'short_text', label: 'Short Text Response' },
  { value: 'long_text', label: 'Long Text Response' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'form', label: 'Form' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
];

/** Client copy of the server's choices rule (orgSpecialRequestsCore). */
const CHOICES_REQUIRED_MESSAGE =
  'A Dropdown Choice or Multiple Choice request requires at least one choice. Add the choices the organization can pick from, or choose a different response type.';

/** Stored choices are plain strings; tolerate {label}/{value} objects on read. */
function toChoiceList(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((o: any) => String(typeof o === 'string' ? o : o?.label || o?.value || ''));
}

interface FormState {
  templateName: string;
  title: string;
  description: string;
  instructions: string;
  targetOrgTypes: OrgTemplateOrgType[];
  requestType: 'Standard' | 'Form';
  responseType: string;
  priority: string;
  requiredDocs: string;
  dropdownOptions: string[];
  formFields: FormBuilderField[];
  defaultDueDays: string;
  blocksWorkspaceAccess: boolean;
  requiresApproval: boolean;
  notifyAdminOnSubmit: boolean;
  notifyAdminOnCompletion: boolean;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  templateName: '',
  title: '',
  description: '',
  instructions: '',
  targetOrgTypes: [],
  requestType: 'Standard',
  responseType: 'file_upload',
  priority: 'Medium',
  requiredDocs: '',
  dropdownOptions: [],
  formFields: [],
  defaultDueDays: '',
  blocksWorkspaceAccess: false,
  requiresApproval: true,
  notifyAdminOnSubmit: false,
  notifyAdminOnCompletion: true,
  isActive: true,
};

function linesToArray(text: string): string[] {
  return text.split('\n').map((s) => s.trim()).filter(Boolean);
}

export default function OrgSpecialRequestTemplatesManager({ canEdit = true }: { canEdit?: boolean }) {
  const [templates, setTemplates] = useState<OrgSpecialRequestTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<OrgSpecialRequestTemplate | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [sendTemplate, setSendTemplate] = useState<OrgSpecialRequestTemplate | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await fetchOrgSpecialRequestTemplates());
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (t: OrgSpecialRequestTemplate) => {
    setEditing(t);
    setForm({
      templateName: t.templateName || '',
      title: t.title || '',
      description: t.description || '',
      instructions: t.instructions || '',
      targetOrgTypes: t.targetOrgTypes || [],
      requestType: t.requestType === 'Form' ? 'Form' : 'Standard',
      responseType: t.responseType || 'file_upload',
      priority: t.priority || 'Medium',
      requiredDocs: (t.requiredDocs || []).join('\n'),
      dropdownOptions: toChoiceList(t.dropdownOptions),
      formFields: Array.isArray(t.formFields) ? t.formFields : [],
      defaultDueDays: t.defaultDueDays != null ? String(t.defaultDueDays) : '',
      blocksWorkspaceAccess: t.blocksWorkspaceAccess === true,
      requiresApproval: t.requiresApproval !== false,
      notifyAdminOnSubmit: t.notifyAdminOnSubmit === true,
      notifyAdminOnCompletion: t.notifyAdminOnCompletion !== false,
      isActive: t.isActive !== false,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.templateName.trim() || !form.title.trim()) {
      notifyToast('error', 'Template name and request title are required');
      return;
    }
    if (form.targetOrgTypes.length === 0) {
      notifyToast('error', 'Select at least one target organization type');
      return;
    }
    if (form.requestType === 'Form' && form.formFields.length === 0) {
      notifyToast('error', 'A Form template requires at least one form field. Add a field in the form builder, or choose the Standard request type.');
      return;
    }
    // Choice-based response types need at least one choice — the
    // same rule the server cores enforce (ORG_CHOICES_REQUIRED_ERROR).
    if (
      form.requestType !== 'Form' &&
      (form.responseType === 'dropdown' || form.responseType === 'multiple_choice') &&
      form.dropdownOptions.map((s) => s.trim()).filter(Boolean).length === 0
    ) {
      notifyToast('error', CHOICES_REQUIRED_MESSAGE);
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<OrgSpecialRequestTemplate> = {
        templateName: form.templateName.trim(),
        title: form.title.trim(),
        description: form.description,
        instructions: form.instructions,
        targetOrgTypes: form.targetOrgTypes,
        requestType: form.requestType,
        responseType: form.requestType === 'Form' ? 'form' : form.responseType,
        priority: form.priority,
        requiredDocs: linesToArray(form.requiredDocs),
        dropdownOptions: form.dropdownOptions.map((s) => s.trim()).filter(Boolean),
        formFields: form.formFields,
        defaultDueDays: form.defaultDueDays.trim() ? Number(form.defaultDueDays) : null,
        blocksWorkspaceAccess: form.blocksWorkspaceAccess,
        requiresApproval: form.requiresApproval,
        notifyAdminOnSubmit: form.notifyAdminOnSubmit,
        notifyAdminOnCompletion: form.notifyAdminOnCompletion,
        isActive: form.isActive,
      };
      if (editing) {
        await updateOrgSpecialRequestTemplate(editing.templateId, payload);
        notifyToast('success', 'Template updated');
      } else {
        await createOrgSpecialRequestTemplate(payload);
        notifyToast('success', 'Template created');
      }
      setShowForm(false);
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t: OrgSpecialRequestTemplate) => {
    try {
      await updateOrgSpecialRequestTemplate(t.templateId, { isActive: t.isActive === false });
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to update template');
    }
  };

  const remove = async (t: OrgSpecialRequestTemplate) => {
    if (!(await confirmDialog(`Delete template "${t.templateName}"? Requests already sent from it are not affected.`))) return;
    try {
      await deleteOrgSpecialRequestTemplate(t.templateId);
      notifyToast('success', 'Template deleted');
      await load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to delete template');
    }
  };

  const visible = templates.filter((t) =>
    !search || `${t.templateName} ${t.title}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <ClipboardList size={20} className="text-indigo-600" /> Organization Special Request Templates
        </h2>
        {canEdit && (
          <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-primary-dark">
            <Plus size={16} /> New Template
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Reusable request templates for Referrer, Sponsor, and Employer organizations. Each template is available only
        for the organization types it targets, and every send creates an independent request per organization.
        These templates are separate from applicant special request templates.
      </p>

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates…"
          className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 py-8 text-center">Loading templates…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center">No templates yet.</p>
      ) : (
        <div className="space-y-3">
          {visible.map((t) => (
            <div key={t.templateId} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800">{t.templateName}</span>
                  {t.isActive === false
                    ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>
                    : <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>}
                  {t.requestType === 'Form' && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Form · {(t.formFields || []).length} field{(t.formFields || []).length === 1 ? '' : 's'}</span>
                  )}
                  {(t.targetOrgTypes || []).map((ty) => (
                    <span key={ty} className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 capitalize">{ty}s</span>
                  ))}
                </div>
                <p className="text-sm text-slate-600 mt-1">{t.title}</p>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500 flex-wrap">
                  <span>Deadline: {t.defaultDueDays != null ? `${t.defaultDueDays} days` : 'None'}</span>
                  {t.blocksWorkspaceAccess && (
                    <span className="flex items-center gap-1 text-red-600 font-medium"><ShieldAlert size={12} /> Blocks workspace access</span>
                  )}
                  {t.requiresApproval !== false && (
                    <span className="flex items-center gap-1 text-amber-600 font-medium"><Lock size={12} /> Requires admin approval</span>
                  )}
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setSendTemplate(t)}
                    disabled={t.isActive === false}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-brand-primary text-white text-xs font-medium hover:bg-brand-primary-dark disabled:opacity-40"
                  >
                    <Send size={13} /> Send
                  </button>
                  <button onClick={() => openEdit(t)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50">
                    <Edit3 size={13} /> Edit
                  </button>
                  <button onClick={() => toggleActive(t)} className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50">
                    {t.isActive === false ? 'Activate' : 'Deactivate'}
                  </button>
                  <button aria-label="Delete template" title="Delete template" onClick={() => remove(t)} className="p-1.5 rounded-lg border border-slate-200 text-red-500 hover:bg-red-50">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <TemplateFormModal
          form={form}
          setForm={setForm}
          editing={!!editing}
          saving={saving}
          onClose={() => setShowForm(false)}
          onSave={save}
        />
      )}
      {sendTemplate && (
        <SendFromTemplateDialog template={sendTemplate} onClose={() => setSendTemplate(null)} />
      )}
    </div>
  );
}

function TemplateFormModal({ form, setForm, editing, saving, onClose, onSave }: {
  form: FormState;
  setForm: (f: FormState) => void;
  editing: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
}) {
  const set = (patch: Partial<FormState>) => setForm({ ...form, ...patch });
  const toggleType = (ty: OrgTemplateOrgType) =>
    set({ targetOrgTypes: form.targetOrgTypes.includes(ty) ? form.targetOrgTypes.filter((x) => x !== ty) : [...form.targetOrgTypes, ty] });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">{editing ? 'Edit Template' : 'New Organization Template'}</h3>
          <button aria-label="Close" title="Close" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-500">Template name</span>
              <input value={form.templateName} onChange={(e) => set({ templateName: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Annual insurance renewal" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-500">Request title</span>
              <input value={form.title} onChange={(e) => set({ title: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Title shown to the organization" />
            </label>
          </div>
          <div>
            <span className="text-xs font-medium text-slate-500">Request type</span>
            <div className="flex gap-2 mt-1">
              {(['Standard', 'Form'] as const).map((rt) => (
                <button
                  key={rt}
                  onClick={() => set({ requestType: rt, responseType: rt === 'Form' ? 'form' : form.responseType })}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${form.requestType === rt ? 'bg-brand-primary text-white border-brand-primary' : 'border-slate-200 text-slate-600'}`}
                >
                  {rt}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {form.requestType === 'Form'
                ? 'The organization completes a form you build below — the same form experience as applicant special requests.'
                : 'The organization responds with text, files, a date, or a dropdown choice.'}
            </p>
          </div>
          <div>
            <span className="text-xs font-medium text-slate-500">Available for organization types</span>
            <div className="flex gap-2 mt-1">
              {ORG_TYPES.map((ty) => (
                <button
                  key={ty.value}
                  onClick={() => toggleType(ty.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${form.targetOrgTypes.includes(ty.value) ? 'bg-brand-primary text-white border-brand-primary' : 'border-slate-200 text-slate-600'}`}
                >
                  {ty.label}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-slate-500">Description / instructions for the organization</span>
            <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={3} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-500">Additional instructions (optional)</span>
            <textarea value={form.instructions} onChange={(e) => set({ instructions: e.target.value })} rows={2} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-500">Response type</span>
              <select value={form.responseType} disabled={form.requestType === 'Form'} onChange={(e) => set({ responseType: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm disabled:opacity-60">
                {RESPONSE_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {form.requestType === 'Form' && <span className="text-[11px] text-slate-500">Form requests always use the form format.</span>}
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-500">Priority</span>
              <select value={form.priority} onChange={(e) => set({ priority: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option>Low</option><option>Medium</option><option>High</option><option>Urgent</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-500">Default deadline (days, blank = none)</span>
              <input type="number" min={1} value={form.defaultDueDays} onChange={(e) => set({ defaultDueDays: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-slate-500">Required documents (one per line)</span>
            <textarea value={form.requiredDocs} onChange={(e) => set({ requiredDocs: e.target.value })} rows={2} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Insurance certificate" />
          </label>
          {(form.responseType === 'dropdown' || form.responseType === 'multiple_choice') && form.requestType !== 'Form' && (
            <div className="block">
              <span className="text-xs font-medium text-slate-500">Choices — the organization picks from these</span>
              <div className="mt-1 border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                <ChoicesEditor
                  options={form.dropdownOptions}
                  onChange={(options) => set({ dropdownOptions: options })}
                />
              </div>
              {form.dropdownOptions.map((s) => s.trim()).filter(Boolean).length === 0 && (
                <p className="text-xs text-red-500 mt-1">{CHOICES_REQUIRED_MESSAGE}</p>
              )}
            </div>
          )}
          {(form.requestType === 'Form' || form.responseType === 'form') && (
            <div>
              <span className="text-xs font-medium text-slate-500">Form fields — the same builder as applicant special request forms</span>
              <div className="mt-1 border border-slate-200 rounded-lg p-3 bg-slate-50/60">
                <FormBuilder
                  fields={form.formFields}
                  onChange={(fields) => set({ formFields: fields })}
                />
              </div>
              {form.requestType === 'Form' && form.formFields.length === 0 && (
                <p className="text-xs text-red-500 mt-1">A Form template requires at least one form field.</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.blocksWorkspaceAccess} onChange={(e) => set({ blocksWorkspaceAccess: e.target.checked })} className="mt-0.5" />
              <span><strong>Blocks workspace access</strong> while outstanding</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.requiresApproval} onChange={(e) => set({ requiresApproval: e.target.checked })} className="mt-0.5" />
              <span><strong>Requires admin approval</strong> after submission</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.notifyAdminOnSubmit} onChange={(e) => set({ notifyAdminOnSubmit: e.target.checked })} className="mt-0.5" />
              <span>Notify me when submitted</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.notifyAdminOnCompletion} onChange={(e) => set({ notifyAdminOnCompletion: e.target.checked })} className="mt-0.5" />
              <span>Notify me when completed</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} className="mt-0.5" />
              <span>Template is active</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600">Cancel</button>
          <button onClick={onSave} disabled={saving} className="px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-primary-dark disabled:opacity-50">
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create template'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SendFromTemplateDialog({ template, onClose }: { template: OrgSpecialRequestTemplate; onClose: () => void }) {
  const [orgType, setOrgType] = useState<OrgTemplateOrgType>(template.targetOrgTypes[0]);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [orgSearch, setOrgSearch] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<OrgBulkSendResult[] | null>(null);

  useEffect(() => {
    setLoadingOrgs(true);
    setSelected(new Set());
    fetchOrgOptions(orgType)
      .then(setOrgs)
      .catch((err: any) => notifyToast('error', err.message || 'Failed to load organizations'))
      .finally(() => setLoadingOrgs(false));
  }, [orgType]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const send = async () => {
    if (selected.size === 0) {
      notifyToast('error', 'Select at least one organization');
      return;
    }
    setSending(true);
    try {
      const res = await sendOrgSpecialRequestsBulk({
        orgType,
        orgIds: [...selected],
        templateId: template.templateId,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      });
      setResults(res.results);
      if (res.failed === 0) notifyToast('success', `Sent to ${res.sent} organization${res.sent === 1 ? '' : 's'}`);
      else notifyToast('error', `Sent to ${res.sent}, failed for ${res.failed} — see details`);
    } catch (err: any) {
      notifyToast('error', err.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const visibleOrgs = orgs.filter((o) => !orgSearch || o.orgName.toLowerCase().includes(orgSearch.toLowerCase()));
  const deadlineText = dueDate
    ? new Date(dueDate).toLocaleDateString()
    : template.defaultDueDays != null
      ? `${template.defaultDueDays} days from today`
      : 'No deadline';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-slate-800">Send “{template.templateName}”</h3>
          <button aria-label="Close" title="Close" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">{template.title}</p>

        {results ? (
          <div className="space-y-2">
            {results.map((r) => {
              const org = orgs.find((o) => o.orgId === r.orgId);
              return (
                <div key={r.orgId} className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${r.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {r.ok ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                  <span className="font-medium">{org?.orgName || r.orgId}</span>
                  {!r.ok && <span className="text-xs">{r.error}</span>}
                </div>
              );
            })}
            <div className="flex justify-end pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium">Done</button>
            </div>
          </div>
        ) : (
          <>
            {template.targetOrgTypes.length > 1 && (
              <div className="flex gap-2 mb-3">
                {template.targetOrgTypes.map((ty) => (
                  <button
                    key={ty}
                    onClick={() => setOrgType(ty)}
                    className={`px-3 py-1.5 rounded-lg text-sm border capitalize ${orgType === ty ? 'bg-brand-primary text-white border-brand-primary' : 'border-slate-200 text-slate-600'}`}
                  >
                    {ty}s
                  </button>
                ))}
              </div>
            )}
            <div className="relative mb-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} placeholder="Search organizations…" className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div className="border border-slate-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-slate-100">
              {loadingOrgs ? (
                <p className="text-sm text-slate-400 p-3">Loading…</p>
              ) : visibleOrgs.length === 0 ? (
                <p className="text-sm text-slate-400 p-3">No organizations found.</p>
              ) : visibleOrgs.map((o) => (
                <label key={o.orgId} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={selected.has(o.orgId)} onChange={() => toggle(o.orgId)} />
                  <span className="flex-1 text-slate-700">{o.orgName}</span>
                  {!o.hasRecipient && <span className="text-[11px] text-amber-600">No main user — send will fail</span>}
                </label>
              ))}
            </div>
            <label className="block mt-3">
              <span className="text-xs font-medium text-slate-500">Deadline override (optional)</span>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </label>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3 text-sm text-slate-600 space-y-1">
              <p><strong>Review before sending:</strong> {selected.size} organization{selected.size === 1 ? '' : 's'} selected, each gets its own independent request.</p>
              <p>Deadline: {deadlineText}</p>
              <p>Blocks workspace access: {template.blocksWorkspaceAccess ? 'Yes' : 'No'} · Requires admin approval: {template.requiresApproval !== false ? 'Yes' : 'No'}</p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600">Cancel</button>
              <button onClick={send} disabled={sending || selected.size === 0} className="px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-primary-dark disabled:opacity-50">
                {sending ? 'Sending…' : `Send to ${selected.size || ''} organization${selected.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
